import { Fragment, useMemo, useState } from 'react';
import { ChevronRight, ChevronsDownUp, ChevronsUpDown, Settings2, Users } from 'lucide-react';
import { BudgetMeter, CellBar } from '@/components/molecules/org-admin/BudgetMeter';
import { useContainerWidth } from '@/components/molecules/org-admin/OrgAdminTable';
import { IconActionButton } from '@/components/molecules/IconActionButton';
import { UsageStatusBadge } from '@/components/atoms/org-admin/UsageStatusBadge';
import {
  OrgAdminEmptyState,
  OrgAdminTableSkeleton,
} from '@/components/molecules/org-admin/OrgAdminStates';
import {
  PlainHeader,
  SortableHeader,
  sortRows,
  useSort,
} from '@/components/molecules/org-admin/OrgAdminTable';
import { cn } from '@/lib/utils';
import type {
  OrgServiceUsageBreakdown,
  OrgServiceUsageReportUser,
  OrgUsageReportMember,
  UsageWindowMonth,
} from '@/services/org-admin';
import { memberAllowance } from '@/services/org-admin/allowance';
import { formatCurrencyUsd, labelForService } from './usageFormat';
import {
  memberSpend,
  memberTokenSplit,
  memberTokens,
  serviceMetric,
  windowPeriodLabel,
  type ServiceMetric,
} from './usageScope';

// Flip to true once budget editing is fully wired up end-to-end.
const SHOW_EDIT_BUDGET = false;

type MemberUsageSortKey = 'member' | 'tokens' | 'spend' | 'available' | 'allowance';

export interface MemberUsageTableProps {
  data: OrgUsageReportMember[];
  /** The selected calendar month — names the window's copy ("this month", "last month", ...). */
  selection: UsageWindowMonth;
  isLoading?: boolean;
  onEditBudget: (member: OrgUsageReportMember) => void;
  serviceByUsers: OrgServiceUsageReportUser[];
  isServiceByUsersLoading?: boolean;
}

const COLUMN_COUNT = SHOW_EDIT_BUDGET ? 7 : 6;

/** Reconstructs the aggregate for the tooltip. */
function tokenSplitTitle(row: OrgUsageReportMember): string {
  const { input, output } = memberTokenSplit(row);
  return `${input.toLocaleString()} in · ${output.toLocaleString()} out`;
}

function ServiceCell({
  row,
  metric,
  currency,
  className,
}: {
  row: OrgServiceUsageBreakdown;
  metric: ServiceMetric;
  currency?: boolean;
  className?: string;
}) {
  const value = serviceMetric(row, metric);
  return (
    <td className={cn('oa-num', className)}>
      {currency ? formatCurrencyUsd(value) : value.toLocaleString()}
    </td>
  );
}

/**
 * Per-member usage.
 *
 * Renamed from `MemberUsageChart`: it has never been a chart, and calling the two data tables in
 * this module "charts" was actively misleading when navigating the code.
 */
export function MemberUsageTable({
  data,
  selection,
  isLoading,
  onEditBudget,
  serviceByUsers,
  isServiceByUsersLoading,
}: MemberUsageTableProps) {
  /*
   * TWO CLOCKS, EACH NAMED IN ITS OWN COLUMN.
   *
   * "Spent" is the reporting month — what the selector above controls. "Available" and "Allowance"
   * are the ENFORCED cycle: backend #327 computes the plan/credit split against `spend_usd_cycle`,
   * and LiteLLM pauses a member when that split reaches zero. This table used to divide month spend
   * by the budget for the meter, on the stated premise that no seat had an anniversary-anchored
   * cycle; every paid seat is a recurring subscription now, so a seat bought on the 17th could read
   * "40% used" here while its member's own banner said "limit reached". The meter now reads
   * `memberAllowance(row).remainingUsd` (services/org-admin/allowance.ts), which is the backend's
   * own answer, and the column header says which clock it is on.
   */
  const periodLabel = windowPeriodLabel(selection);
  // Heaviest consumers first is the useful default here — an admin opens this to find who is
  // burning the budget, not to read an alphabetical roster.
  const { sort, toggle } = useSort<MemberUsageSortKey>({ key: 'tokens', direction: 'desc' });

  const sorted = useMemo(
    () =>
      sortRows(data, sort, (row, key) => {
        switch (key) {
          case 'member':
            return row.email ?? row.user_id;
          case 'tokens':
            return memberTokens(row);
          case 'spend':
            return memberSpend(row);
          case 'available':
            // Unknown sorts below a genuine $0: a row the backend could not answer for is not
            // "the most exhausted seat".
            return memberAllowance(row).remainingUsd ?? -1;
          case 'allowance':
            return row.budget_usd;
        }
      }),
    [data, sort],
  );

  const serviceByUserId = useMemo(() => {
    const map = new Map<string, OrgServiceUsageReportUser>();
    serviceByUsers.forEach((user) => map.set(user.user_id, user));
    return map;
  }, [serviceByUsers]);

  const [expandedUserIds, setExpandedUserIds] = useState<Set<string>>(new Set());

  /*
   * Bars are scaled to the largest value IN VIEW, not to a fixed ceiling — the question this column
   * answers is "who is big relative to whom", and a shared absolute scale would flatten every row
   * to a sliver in an org with one heavy user.
   */
  /*
   * Bars and the meter are an ENHANCEMENT and need room to be one.
   *
   * The numbers alone are complete; the visual encoding is what makes them comparable at a glance.
   * Below this width the fixed columns already over-subscribe the panel — adding a meter crushed
   * the Member column to zero and pushed the table past the viewport, which the QA sweep caught at
   * 375px. So the enhancement is dropped there rather than the data.
   */
  const { ref: tableRef, width: panelWidth } = useContainerWidth<HTMLElement>();
  const showBars = panelWidth === null || panelWidth >= 560;

  const maxTokens = useMemo(() => Math.max(0, ...data.map((row) => memberTokens(row))), [data]);
  const maxSpend = useMemo(() => Math.max(0, ...data.map((row) => memberSpend(row))), [data]);

  const toggleExpanded = (userId: string) => {
    setExpandedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // Every row that actually has (or might have, while still loading) a service breakdown to show —
  // the same `canExpand` condition each row computes individually below.
  const expandableUserIds = useMemo(
    () =>
      sorted
        .filter((row) => isServiceByUsersLoading || (serviceByUserId.get(row.user_id)?.by_service.length ?? 0) > 0)
        .map((row) => row.user_id),
    [sorted, serviceByUserId, isServiceByUsersLoading],
  );
  const allExpanded =
    expandableUserIds.length > 0 && expandableUserIds.every((id) => expandedUserIds.has(id));

  const toggleExpandAll = () => {
    setExpandedUserIds(allExpanded ? new Set() : new Set(expandableUserIds));
  };

  return (
    <section
      ref={tableRef}
      className="oa-panel flex flex-col"
      aria-label="Usage by member"
    >
      <header className="oa-toolbar flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2.5">
        <div className="min-w-0 flex-1 basis-64">
          <h3 className="oa-title">Usage by member</h3>
          <p className="oa-meta mt-0.5">
            Tokens and spend {periodLabel}. Available and allowance are each member&rsquo;s current
            cycle — the clock their limit is enforced on. Expand a row for its per-service breakdown.
          </p>
        </div>
        {/*
          Expand-all, which the per-row chevrons cannot substitute for: comparing per-service costs
          ACROSS members is the reason the drill-down exists, and doing it a row at a time means the
          previous row has collapsed by the time you open the next one.

          Hidden rather than disabled when nothing is expandable — a control whose only possible
          outcome is "nothing happens" is noise, and unlike the row actions there is no "why" to
          explain in a tooltip. `aria-expanded` reports the group's state so a screen reader gets the
          same information the icon carries.
        */}
        {expandableUserIds.length > 0 && (
          <button
            type="button"
            onClick={toggleExpandAll}
            aria-expanded={allExpanded}
            className="oa-interactive inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2 text-[11px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          >
            {allExpanded ? (
              <ChevronsDownUp className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronsUpDown className="h-3.5 w-3.5" aria-hidden />
            )}
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </header>

      {/*
        No fixed height. The table used to live in an `h-64` box regardless of the space available,
        so a 1080px screen showed five rows above a large empty gap while the rest of the roster
        stayed hidden behind an inner scrollbar nested inside the page's own scrollbar.
      */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <OrgAdminTableSkeleton
            rows={5}
            columns={['30%', '90px', '90px', '140px', '110px']}
            label="Loading member usage…"
          />
        ) : sorted.length === 0 ? (
          <OrgAdminEmptyState
            icon={Users}
            // Names the period, not "this window" — the range is a calendar month now, and "window"
            // is vocabulary this module retired everywhere else.
            title={`No usage ${periodLabel}`}
            description={`Nobody in the org has spent tokens ${periodLabel}.`}
          />
        ) : (
          <table className="oa-table">
            <caption className="sr-only">
              Token consumption and spend per member {periodLabel}, with what each member can still
              spend this cycle and their allowance split between plan and credits
            </caption>
            <thead>
              <tr>
                <PlainHeader label="Expand row" width="36px" srOnly />
                <SortableHeader columnKey="member" label="Member" sort={sort} onSort={toggle} />
                <SortableHeader columnKey="tokens" label="Tokens" sort={sort} onSort={toggle} align="right" width="110px" />
                <SortableHeader columnKey="spend" label="Spent" sort={sort} onSort={toggle} align="right" width="100px" />
                <SortableHeader columnKey="available" label="Available · cycle" sort={sort} onSort={toggle} align="right" width="150px" />
                <SortableHeader columnKey="allowance" label="Allowance" sort={sort} onSort={toggle} align="right" width="120px" />
                {SHOW_EDIT_BUDGET && <PlainHeader label="Edit budget" width="44px" srOnly />}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const serviceRows = serviceByUserId.get(row.user_id)?.by_service ?? [];
                const isExpanded = expandedUserIds.has(row.user_id);
                const canExpand = isServiceByUsersLoading || serviceRows.length > 0;
                const allowance = memberAllowance(row);
                /*
                 * "Limit reached" is the backend's remaining hitting zero on a capped seat — the
                 * condition LiteLLM pauses on — not month spend crossing the budget.
                 */
                const limitReached =
                  allowance.totalUsd > 0 && allowance.remainingUsd !== null && allowance.remainingUsd <= 0;
                const usedThisCycle =
                  allowance.remainingUsd === null
                    ? null
                    : Math.max(0, allowance.totalUsd - allowance.remainingUsd);
                const label = row.email ?? row.user_id;

                return (
                  <Fragment key={row.user_id}>
                    <tr>
                      <td>
                        {canExpand && (
                          <button
                            type="button"
                            // `aria-expanded` + `aria-controls` are what make a disclosure row
                            // navigable without sight; the old chevron was an unlabelled ghost
                            // button with only a `title`.
                            aria-expanded={isExpanded}
                            aria-controls={`services-${row.user_id}`}
                            aria-label={`${isExpanded ? 'Hide' : 'Show'} service breakdown for ${label}`}
                            onClick={() => toggleExpanded(row.user_id)}
                            className="grid h-6 w-6 place-items-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          >
                            <ChevronRight
                              className={cn(
                                'h-3.5 w-3.5 transition-transform duration-150 motion-reduce:transition-none',
                                isExpanded && 'rotate-90',
                              )}
                              aria-hidden
                            />
                          </button>
                        )}
                      </td>
                      <td>
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="truncate font-medium">{label}</span>
                          <UsageStatusBadge blocked={row.blocked} overBudget={limitReached} />
                        </div>
                      </td>
                      {/* The aggregate carries its own components in the tooltip — the same habit the
                          Observability report uses for a combined token cell. */}
                      <td className="oa-num" title={tokenSplitTitle(row)}>
                        {showBars ? (
                          <CellBar value={memberTokens(row)} max={maxTokens}>
                            {memberTokens(row).toLocaleString()}
                          </CellBar>
                        ) : (
                          memberTokens(row).toLocaleString()
                        )}
                      </td>
                      <td className="oa-num">
                        {showBars ? (
                          <CellBar value={memberSpend(row)} max={maxSpend}>
                            {formatCurrencyUsd(memberSpend(row))}
                          </CellBar>
                        ) : (
                          formatCurrencyUsd(memberSpend(row))
                        )}
                      </td>
                      <td className="oa-num">
                        {allowance.totalUsd <= 0 ? (
                          <span className="oa-meta">No limit</span>
                        ) : allowance.remainingUsd === null ? (
                          /* The payload could not say — an em dash, never a derived figure. */
                          <span className="oa-meta">—</span>
                        ) : (
                          <>
                            <span
                              className={cn(
                                'font-semibold',
                                limitReached ? 'text-destructive-strong' : 'text-foreground',
                              )}
                            >
                              {formatCurrencyUsd(allowance.remainingUsd)}
                            </span>
                            {/* Which pot the remainder sits in. Spend draws the plan down first, so
                                once the plan part is $0 everything left is carried-over credit. */}
                            {allowance.hasBreakdown && (
                              <span className="oa-meta block">
                                plan {formatCurrencyUsd(allowance.planRemainingUsd!)} · credits{' '}
                                {formatCurrencyUsd(allowance.creditRemainingUsd!)}
                              </span>
                            )}
                            {showBars && usedThisCycle !== null && (
                              <BudgetMeter
                                size="sm"
                                className="ml-auto mt-1 w-20 max-w-full"
                                spentUsd={usedThisCycle}
                                budgetUsd={allowance.totalUsd}
                                label={label}
                              />
                            )}
                          </>
                        )}
                      </td>
                      <td className="oa-num">
                        {allowance.totalUsd <= 0 ? (
                          <span className="oa-meta">—</span>
                        ) : (
                          <>
                            {formatCurrencyUsd(allowance.totalUsd)}
                            {allowance.hasBreakdown && (
                              <span className="oa-meta block">
                                plan {formatCurrencyUsd(allowance.planBudgetUsd!)} + credits{' '}
                                {formatCurrencyUsd(allowance.creditBalanceUsd!)}
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      {SHOW_EDIT_BUDGET && (
                        <td>
                          <div className="oa-row-actions flex justify-end">
                            <IconActionButton
                              label={`Edit budget for ${label}`}
                              icon={Settings2}
                              size="sm"
                              onClick={() => onEditBudget(row)}
                            />
                          </div>
                        </td>
                      )}
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td colSpan={COLUMN_COUNT} className="!p-0">
                          <div id={`services-${row.user_id}`} className="oa-subtable-shell px-[var(--oa-gutter)] py-2">
                            {isServiceByUsersLoading ? (
                              <p className="oa-meta py-1">Loading service breakdown…</p>
                            ) : serviceRows.length === 0 ? (
                              <p className="oa-meta py-1">No service activity {periodLabel}.</p>
                            ) : (
                              <table className="oa-table oa-subtable">
                                <caption className="sr-only">Service breakdown for {label}</caption>
                                <thead>
                                  <tr>
                                    <PlainHeader label="Service" />
                                    <PlainHeader label="Runs" align="right" width="70px" />
                                    <PlainHeader label="Failed" align="right" width="70px" />
                                    <PlainHeader label="Input" align="right" width="100px" />
                                    <PlainHeader label="Output" align="right" width="100px" />
                                    <PlainHeader label="Cost" align="right" width="90px" />
                                  </tr>
                                </thead>
                                <tbody>
                                  {serviceRows.map((service) => {
                                    const failed = serviceMetric(service, 'failed');
                                    return (
                                      <tr key={service.service}>
                                        <td className="font-medium">
                                          {labelForService(service.service)}
                                        </td>
                                        <ServiceCell row={service} metric="runs" />
                                        <ServiceCell
                                          row={service}
                                          metric="failed"
                                          className={failed ? 'text-warning-strong' : undefined}
                                        />
                                        <ServiceCell row={service} metric="input" />
                                        <ServiceCell row={service} metric="output" />
                                        <ServiceCell row={service} metric="cost" currency />
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
